import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

/* ─────────────────────────────────────────────
   MATRIX RAIN
───────────────────────────────────────────── */
const MatrixRain = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize();
    const cols = Math.floor(c.width / 20);
    const drops = Array(cols).fill(1);
    const ch = '01₿$€¥BTC ETH XRP';
    const draw = () => {
      ctx.fillStyle = 'rgba(6,16,29,0.06)';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#00e676';
      ctx.font = '14px monospace';
      drops.forEach((y, i) => {
        ctx.fillText(ch[Math.floor(Math.random() * ch.length)], i * 20, y * 20);
        if (y * 20 > c.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      });
    };
    const id = setInterval(draw, 35);
    window.addEventListener('resize', resize);
    return () => { clearInterval(id); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none opacity-20" style={{ zIndex: 0 }} />;
};

/* ─────────────────────────────────────────────
   LOGO REAL
───────────────────────────────────────────── */
const AegisLogo = ({ size = 120 }) => (
  <img
    src="/images/aegis-logo.png"
    alt="Aegis Trading Platform"
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

/* ─────────────────────────────────────────────
   1. TRADING TERMINAL
───────────────────────────────────────────── */
const TradingTerminal = () => (
  <svg viewBox="0 0 380 240" fill="none" width="100%">
    <defs>
      <linearGradient id="termBg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#080f1a"/>
        <stop offset="100%" stopColor="#030810"/>
      </linearGradient>
      <filter id="glowG"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      <filter id="glowC"><feGaussianBlur stdDeviation="1.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <rect width="380" height="240" rx="12" fill="url(#termBg)" stroke="#1a3a5c" strokeWidth="1.5"/>
    <rect width="380" height="28" rx="12" fill="#0a1828"/>
    <rect y="12" width="380" height="16" fill="#0a1828"/>
    <circle cx="14" cy="14" r="4.5" fill="#ef4444" opacity="0.8"/>
    <circle cx="26" cy="14" r="4.5" fill="#FFD600" opacity="0.8"/>
    <circle cx="38" cy="14" r="4.5" fill="#10b981" opacity="0.8"/>
    <text x="100" y="18" fill="#00D4FF" fontSize="9" fontFamily="monospace" fontWeight="bold">BTC/USDT</text>
    <text x="175" y="18" fill="#10b981" fontSize="9" fontFamily="monospace" fontWeight="bold">63,480.50</text>
    <text x="235" y="18" fill="#10b981" fontSize="8" fontFamily="monospace">▲ +2.48%</text>
    <text x="296" y="18" fill="#52525B" fontSize="7" fontFamily="monospace">VOL: 42.8B</text>
    <text x="345" y="18" fill="#10b981" fontSize="7" fontFamily="monospace">● LIVE</text>
    <rect x="8" y="32" width="260" height="140" rx="4" fill="#06101d" stroke="#0f2845" strokeWidth="0.8"/>
    {[0,35,70,105,140].map(y => (
      <line key={y} x1="8" y1={32+y} x2="268" y2={32+y} stroke="#0f2845" strokeWidth="0.6" strokeDasharray="3 4"/>
    ))}
    {[['64.2K',32],['63.8K',67],['63.4K',102],['63.0K',137]].map(([p,y]) => (
      <text key={p} x="270" y={y+4} fill="#52525B" fontSize="6" fontFamily="monospace">{p}</text>
    ))}
    <path d="M8,55 Q50,50 90,58 Q130,65 170,52 Q210,45 260,60" fill="none" stroke="#1e3a5f" strokeWidth="1.2" strokeDasharray="3 2"/>
    <path d="M8,95 Q50,88 90,98 Q130,105 170,92 Q210,85 260,100" fill="none" stroke="#1e3a5f" strokeWidth="1.2" strokeDasharray="3 2"/>
    <path d="M8,85 Q40,80 80,88 Q120,96 160,82 Q200,72 260,78" fill="none" stroke="#00D4FF" strokeWidth="1.2" opacity="0.7"/>
    {[
      [12,80,92,75,95,'#ef4444'],[26,92,80,96,76,'#10b981'],[40,80,70,83,66,'#10b981'],
      [54,70,78,65,82,'#ef4444'],[68,78,65,82,60,'#10b981'],[82,65,55,68,50,'#10b981'],
      [96,55,62,50,66,'#ef4444'],[110,62,52,65,48,'#10b981'],[124,52,42,55,38,'#10b981'],
      [138,42,50,38,54,'#ef4444'],[152,50,40,53,36,'#10b981'],[166,40,30,43,26,'#10b981'],
      [180,30,38,26,42,'#ef4444'],[194,38,28,41,24,'#10b981'],[208,28,20,31,16,'#10b981'],
      [222,20,28,16,32,'#ef4444'],[236,28,18,31,14,'#10b981'],[250,18,10,21,6,'#10b981'],
    ].map(([x,o,c,h,l,col],i) => (
      <g key={i} filter="url(#glowC)">
        <line x1={x+5} y1={32+h} x2={x+5} y2={32+l} stroke={col} strokeWidth="1.2"/>
        <rect x={x} y={32+Math.min(o,c)} width="10" height={Math.max(Math.abs(o-c),2)} rx="1" fill={col} opacity="0.9"/>
      </g>
    ))}
    <circle cx="182" cy="155" r="5" fill="none" stroke="#10b981" strokeWidth="1.5" filter="url(#glowG)"/>
    <text x="186" y="168" fill="#10b981" fontSize="7" fontFamily="monospace">BUY</text>
    <rect x="272" y="32" width="100" height="140" rx="4" fill="#06101d" stroke="#0f2845" strokeWidth="0.8"/>
    <text x="292" y="46" fill="#A1A1AA" fontSize="7" fontFamily="monospace">LIBRO DE ORDENES</text>
    {[[63560,'4.2','#ef4444',90],[63520,'8.1','#ef4444',70],[63490,'2.7','#ef4444',50],[63470,'12.4','#ef4444',30]].map(([p,q,col,w],i) => (
      <g key={i}>
        <rect x="272" y={52+i*14} width={w} height="12" rx="1" fill="#ef4444" opacity="0.12"/>
        <text x="276" y={61+i*14} fill={col} fontSize="6.5" fontFamily="monospace">{p}</text>
        <text x="330" y={61+i*14} fill="#A1A1AA" fontSize="6.5" fontFamily="monospace">{q}</text>
      </g>
    ))}
    <text x="286" y="117" fill="#FFD600" fontSize="6.5" fontFamily="monospace" fontWeight="bold">63,450 — SPREAD</text>
    {[[63430,'6.3','#10b981',60],[63410,'9.8','#10b981',80],[63390,'3.1','#10b981',40],[63360,'15.2','#10b981',100]].map(([p,q,col,w],i) => (
      <g key={i}>
        <rect x="272" y={122+i*14} width={w} height="12" rx="1" fill="#10b981" opacity="0.12"/>
        <text x="276" y={131+i*14} fill={col} fontSize="6.5" fontFamily="monospace">{p}</text>
        <text x="330" y={131+i*14} fill="#A1A1AA" fontSize="6.5" fontFamily="monospace">{q}</text>
      </g>
    ))}
    <rect x="8" y="178" width="364" height="54" rx="4" fill="#06101d" stroke="#0f2845" strokeWidth="0.8"/>
    <text x="14" y="191" fill="#A1A1AA" fontSize="7" fontFamily="monospace">POSICIONES ABIERTAS</text>
    {[
      ['BTC/USDT','LONG','0.05','63,200','63,480','+14.00','#10b981'],
      ['ETH/USDT','SHORT','0.8','3,420','3,398','+17.60','#10b981'],
      ['SOL/USDT','LONG','2.0','148.20','146.50','-3.40','#ef4444'],
    ].map(([sym,side,qty,entry,now,pnl,col],i) => (
      <g key={i}>
        <text x="14"  y={204+i*11} fill="white"  fontSize="6.5" fontFamily="monospace">{sym}</text>
        <text x="76"  y={204+i*11} fill={side==='LONG'?'#10b981':'#ef4444'} fontSize="6.5" fontFamily="monospace">{side}</text>
        <text x="112" y={204+i*11} fill="#A1A1AA" fontSize="6.5" fontFamily="monospace">{qty}</text>
        <text x="148" y={204+i*11} fill="#A1A1AA" fontSize="6.5" fontFamily="monospace">{entry}</text>
        <text x="204" y={204+i*11} fill="white"   fontSize="6.5" fontFamily="monospace">{now}</text>
        <text x="258" y={204+i*11} fill={col}      fontSize="6.5" fontFamily="monospace" fontWeight="bold">{pnl}</text>
        <rect x="308" y={196+i*11} width="30" height="10" rx="2" fill="#ef4444" fillOpacity="0.15" stroke="#ef4444" strokeWidth="0.5"/>
        <text x="316" y={204+i*11} fill="#ef4444" fontSize="6" fontFamily="monospace">Cerrar</text>
        <rect x="342" y={196+i*11} width="24" height="10" rx="2" fill="#10b981" fillOpacity="0.15" stroke="#10b981" strokeWidth="0.5"/>
        <text x="347" y={204+i*11} fill="#10b981" fontSize="6" fontFamily="monospace">+TP</text>
      </g>
    ))}
  </svg>
);

/* ─────────────────────────────────────────────
   2. RED NEURONAL
───────────────────────────────────────────── */
const NeuralBrain = () => {
  const layers = [
    { n: 4, x: 52 },
    { n: 7, x: 130 },
    { n: 7, x: 210 },
    { n: 5, x: 288 },
    { n: 2, x: 360 },
  ];
  const H = 180;
  const nodeY = (layer, i) => H/2 + (i - (layer.n - 1) / 2) * (H / (layer.n + 1));
  const nodes = layers.map(l => Array.from({ length: l.n }, (_, i) => ({ x: l.x, y: nodeY(l, i) })));
  const edges = [];
  for (let l = 0; l < layers.length - 1; l++)
    for (let a = 0; a < layers[l].n; a++)
      for (let b = 0; b < layers[l+1].n; b++)
        edges.push({ x1: nodes[l][a].x, y1: nodes[l][a].y, x2: nodes[l+1][b].x, y2: nodes[l+1][b].y });
  return (
    <svg viewBox="0 0 420 180" fill="none" width="100%">
      <defs>
        <radialGradient id="ng1" cx="35%" cy="35%"><stop offset="0%" stopColor="#4fffcf"/><stop offset="100%" stopColor="#10b981"/></radialGradient>
        <radialGradient id="ng2" cx="35%" cy="35%"><stop offset="0%" stopColor="#5ad8ff"/><stop offset="100%" stopColor="#00D4FF"/></radialGradient>
        <radialGradient id="ng3" cx="35%" cy="35%"><stop offset="0%" stopColor="#ffe680"/><stop offset="100%" stopColor="#FFD600"/></radialGradient>
        <filter id="nGlow"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <filter id="eGlow"><feGaussianBlur stdDeviation="1.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <ellipse cx="210" cy="90" rx="180" ry="75" fill="#10b981" fillOpacity="0.04"/>
      {edges.map((e, i) => (
        <line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke="#10b981" strokeWidth="0.5" opacity="0.18"/>
      ))}
      {/* Active paths */}
      {[
        [[0,0],[1,1],[2,2],[3,2],[4,0]],
        [[0,2],[1,4],[2,4],[3,3],[4,1]],
        [[0,1],[1,6],[2,0],[3,1],[4,0]],
      ].map((path, pi) => {
        const colors = ['#10b981','#00D4FF','#FFD600'];
        return path.slice(0,-1).map(([li,ni], step) => {
          const [nl, nn] = path[step+1];
          const p1 = nodes[li][Math.min(ni, nodes[li].length-1)];
          const p2 = nodes[nl][Math.min(nn, nodes[nl].length-1)];
          return (
            <line key={`${pi}-${step}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke={colors[pi]} strokeWidth="1.5" opacity="0.6" filter="url(#eGlow)"/>
          );
        });
      })}
      {nodes.map((layer, li) => layer.map((n, ni) => (
        <circle key={`${li}-${ni}`} cx={n.x} cy={n.y} r={li===0||li===4?8:6}
          fill={li===0?'url(#ng2)':li===4?'url(#ng3)':'url(#ng1)'}
          filter="url(#nGlow)" opacity="0.85"/>
      )))}
      <text x="52"  y="172" fill="#00D4FF" fontSize="7" fontFamily="monospace" textAnchor="middle">MERCADO</text>
      <text x="130" y="172" fill="#10b981" fontSize="7" fontFamily="monospace" textAnchor="middle">CAPA 1</text>
      <text x="210" y="172" fill="#10b981" fontSize="7" fontFamily="monospace" textAnchor="middle">CAPA 2</text>
      <text x="288" y="172" fill="#10b981" fontSize="7" fontFamily="monospace" textAnchor="middle">POLICY</text>
      <text x="360" y="172" fill="#FFD600" fontSize="7" fontFamily="monospace" textAnchor="middle">ACCION</text>
      <rect x="140" y="6" width="140" height="22" rx="4" fill="#10b981" fillOpacity="0.1" stroke="#10b981" strokeWidth="0.6"/>
      <text x="210" y="13" fill="#10b981" fontSize="7" fontFamily="monospace" textAnchor="middle">PPO · Stable-Baselines3 · Keras</text>
      <text x="210" y="23" fill="#4fffcf" fontSize="7.5" fontFamily="monospace" textAnchor="middle" fontWeight="bold">400,000+ muestras de entrenamiento</text>
    </svg>
  );
};

/* ─────────────────────────────────────────────
   3. CHAT ATENEA
───────────────────────────────────────────── */
const AtenеaChat = () => (
  <div className="w-full font-mono text-xs">
    <div className="bg-[#06101d] border border-[#0f3a5f] rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#0a1828] border-b border-[#0f3a5f]">
        <div className="relative">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#10b981] to-[#00D4FF] flex items-center justify-center text-black font-bold text-sm">A</div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#10b981] rounded-full border-2 border-[#0a1828]"/>
        </div>
        <div>
          <p className="text-[#10b981] font-bold text-xs tracking-widest">ATENEA AI v3</p>
          <p className="text-[#52525B] text-[10px]">Cerebro central · Analizando mercado</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse"/>
          <span className="text-[#10b981] text-[9px]">EN LINEA</span>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className="w-5 h-5 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center text-[#10b981] text-[8px] font-bold shrink-0 mt-0.5">A</div>
          <div className="bg-[#0a1828] border border-[#0f3a5f] rounded-xl rounded-tl-none px-3 py-2 max-w-[88%]">
            <p className="text-[#b0f0d8] text-[10px] leading-relaxed">Regimen actual: <span className="text-[#FFD600] font-bold">IMPULSO ALCISTA</span> — RSI 58, volumen 1.8x promedio. BTC rompe resistencia $63,200. Probabilidad de continuacion: <span className="text-[#10b981] font-bold">74%</span></p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/25 rounded-xl rounded-tr-none px-3 py-2 max-w-[80%]">
            <p className="text-[#93d9ff] text-[10px]">Que estrategia recomiendas para ETH ahora?</p>
          </div>
          <div className="w-5 h-5 rounded-full bg-[#00D4FF]/20 border border-[#00D4FF]/40 flex items-center justify-center text-[#00D4FF] text-[8px] shrink-0 mt-0.5">U</div>
        </div>
        <div className="flex gap-2">
          <div className="w-5 h-5 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center text-[#10b981] text-[8px] font-bold shrink-0 mt-0.5">A</div>
          <div className="bg-[#0a1828] border border-[#0f3a5f] rounded-xl rounded-tl-none px-3 py-2 max-w-[88%]">
            <p className="text-[#b0f0d8] text-[10px] leading-relaxed mb-2">Para ETH/USDT en 4H recomiendo:</p>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {[['Entry','$3,380'],['Stop','$3,298 (-2.4%)'],['Target','$3,520 (+4.1%)'],['R:R','1:1.7']].map(([l,v]) => (
                <div key={l} className="bg-[#06101d] rounded px-2 py-1">
                  <p className="text-[#52525B] text-[8px]">{l}</p>
                  <p className="text-white text-[9px] font-bold">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[#10b981] text-[9px]">Confianza: 68% · Estrategia: Momentum EMA 20/50</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 py-3 border-t border-[#0f3a5f] bg-[#06101d]">
        <span className="text-[#27272A] text-[10px] flex-1">Pregunta a Atenea — analiza, predice, crea estrategias...</span>
        <div className="w-1.5 h-4 bg-[#10b981] rounded-sm opacity-80"/>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   4. ARBITRAJE
───────────────────────────────────────────── */
const ArbFlow = () => (
  <svg viewBox="0 0 380 210" fill="none" width="100%">
    <defs>
      <radialGradient id="exA" cx="50%" cy="50%"><stop offset="0%" stopColor="#163a5f"/><stop offset="100%" stopColor="#0a1828"/></radialGradient>
      <radialGradient id="exB" cx="50%" cy="50%"><stop offset="0%" stopColor="#1a3a20"/><stop offset="100%" stopColor="#0a1828"/></radialGradient>
      <radialGradient id="exC" cx="50%" cy="50%"><stop offset="0%" stopColor="#3a3020"/><stop offset="100%" stopColor="#0a1828"/></radialGradient>
      <filter id="flowGlow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      <marker id="arrowG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
        <polygon points="0,0 7,3.5 0,7" fill="#10b981"/>
      </marker>
      <marker id="arrowY" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
        <polygon points="0,0 7,3.5 0,7" fill="#FFD600"/>
      </marker>
    </defs>
    <circle cx="70" cy="105" r="52" fill="url(#exA)" stroke="#00D4FF" strokeWidth="1.5" opacity="0.9"/>
    <text x="70" y="93"  fill="#00D4FF" fontSize="8" fontFamily="monospace" textAnchor="middle" fontWeight="bold">BINANCE</text>
    <text x="70" y="105" fill="#52525B" fontSize="7" fontFamily="monospace" textAnchor="middle">SPOT</text>
    <text x="70" y="118" fill="white"   fontSize="11" fontFamily="monospace" textAnchor="middle" fontWeight="bold">$63,100</text>
    <text x="70" y="130" fill="#ef4444" fontSize="7" fontFamily="monospace" textAnchor="middle">BTC/USDT</text>
    <circle cx="310" cy="62" r="52" fill="url(#exB)" stroke="#10b981" strokeWidth="1.5" opacity="0.9"/>
    <text x="310" y="50" fill="#10b981" fontSize="8" fontFamily="monospace" textAnchor="middle" fontWeight="bold">BYBIT</text>
    <text x="310" y="62" fill="#52525B" fontSize="7" fontFamily="monospace" textAnchor="middle">FUTUROS PERP</text>
    <text x="310" y="75" fill="white"   fontSize="11" fontFamily="monospace" textAnchor="middle" fontWeight="bold">$63,420</text>
    <text x="310" y="87" fill="#10b981" fontSize="7" fontFamily="monospace" textAnchor="middle">BTC-PERP</text>
    <circle cx="310" cy="155" r="52" fill="url(#exC)" stroke="#FFD600" strokeWidth="1.5" opacity="0.9"/>
    <text x="310" y="143" fill="#FFD600" fontSize="8" fontFamily="monospace" textAnchor="middle" fontWeight="bold">OKX</text>
    <text x="310" y="155" fill="#52525B" fontSize="7" fontFamily="monospace" textAnchor="middle">SPOT</text>
    <text x="310" y="168" fill="white"   fontSize="11" fontFamily="monospace" textAnchor="middle" fontWeight="bold">$63,190</text>
    <text x="310" y="180" fill="#FFD600" fontSize="7" fontFamily="monospace" textAnchor="middle">BTC/USDT</text>
    <path d="M120,78 Q190,52 258,62" stroke="#10b981" strokeWidth="2" markerEnd="url(#arrowG)" filter="url(#flowGlow)" opacity="0.9"/>
    <path d="M120,130 Q190,155 258,155" stroke="#FFD600" strokeWidth="2" markerEnd="url(#arrowY)" filter="url(#flowGlow)" opacity="0.9"/>
    <rect x="148" y="86" width="84" height="42" rx="8" fill="#06101d" stroke="#10b981" strokeWidth="1.2"/>
    <text x="190" y="100" fill="#A1A1AA" fontSize="7"  fontFamily="monospace" textAnchor="middle">SPREAD</text>
    <text x="190" y="114" fill="#10b981" fontSize="14" fontFamily="monospace" textAnchor="middle" fontWeight="bold">+$320</text>
    <text x="190" y="123" fill="#52525B" fontSize="6.5" fontFamily="monospace" textAnchor="middle">por BTC · ~0.5%</text>
    <rect x="110" y="196" width="160" height="12" rx="3" fill="#0a1828" stroke="#163a5f" strokeWidth="0.8"/>
    <text x="190" y="205" fill="#52525B" fontSize="7" fontFamily="monospace" textAnchor="middle">Ejecucion simultanea · menos de 200ms · Comisiones incluidas</text>
  </svg>
);

/* ─────────────────────────────────────────────
   5. BOTS COMMAND CENTER
───────────────────────────────────────────── */
const BotCenter = () => (
  <div className="w-full space-y-2">
    <div className="grid grid-cols-3 gap-2 mb-3">
      {[['P&L HOY','+$284.50','#10b981'],['BOTS ACTIVOS','4 / 6','#00D4FF'],['WIN RATE','68.4%','#FFD600']].map(([l,v,c]) => (
        <div key={l} className="bg-[#06101d] border border-[#0f2845] rounded-lg p-2 text-center">
          <p className="text-[#52525B] text-[8px] uppercase tracking-wide">{l}</p>
          <p className="font-bold text-sm font-mono mt-1" style={{color:c}}>{v}</p>
        </div>
      ))}
    </div>
    {[
      { name:'VWAP Master',  sym:'BTC/USDT', tf:'15m', pnl:'+$142.80', trades:28, wr:'71%', status:'running', bars:[40,55,35,70,60,80,65,90] },
      { name:'Momentum AI',  sym:'ETH/USDT', tf:'1h',  pnl:'+$89.40',  trades:14, wr:'64%', status:'running', bars:[30,50,45,65,40,70,55,75] },
      { name:'Grid Scalper', sym:'SOL/USDT', tf:'5m',  pnl:'-$18.30',  trades:42, wr:'48%', status:'paused',  bars:[60,45,50,35,40,30,25,20] },
      { name:'Mean Rev Z',   sym:'BNB/USDT', tf:'4h',  pnl:'+$70.60',  trades:8,  wr:'75%', status:'running', bars:[20,35,45,40,55,60,70,80] },
    ].map((b) => {
      const isUp = b.pnl.startsWith('+');
      const maxB = Math.max(...b.bars);
      return (
        <div key={b.name} className="bg-[#06101d] border border-[#0f2845] rounded-xl px-3 py-2 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full shrink-0" style={{
            background: b.status==='running' ? '#10b981' : '#52525B',
            boxShadow: b.status==='running' ? '0 0 6px #10b981' : 'none',
          }}/>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-white text-xs font-bold">{b.name}</span>
              <span className="text-[#52525B] text-[9px]">{b.sym} · {b.tf}</span>
            </div>
            <p className="text-[#52525B] text-[9px]">WR {b.wr} · {b.trades} trades</p>
          </div>
          <svg width="52" height="28" viewBox="0 0 52 28">
            {b.bars.map((v, bi) => (
              <rect key={bi} x={bi*6+1} y={28-(v/maxB)*26} width="4" height={(v/maxB)*26}
                rx="1" fill={isUp?'#10b981':'#ef4444'} opacity="0.7"/>
            ))}
          </svg>
          <p className="font-bold text-sm font-mono shrink-0" style={{color:isUp?'#10b981':'#ef4444'}}>{b.pnl}</p>
        </div>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────
   6. GLOBO DATOS EN VIVO
───────────────────────────────────────────── */
const LiveGlobe = () => (
  <svg viewBox="0 0 340 220" fill="none" width="100%">
    <defs>
      <radialGradient id="globeFill" cx="38%" cy="35%">
        <stop offset="0%" stopColor="#0d3a5c"/>
        <stop offset="60%" stopColor="#071828"/>
        <stop offset="100%" stopColor="#030c14"/>
      </radialGradient>
      <radialGradient id="globeAtmo" cx="50%" cy="50%">
        <stop offset="70%" stopColor="transparent"/>
        <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.12"/>
      </radialGradient>
      <filter id="pingGlow"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <circle cx="170" cy="110" r="90" fill="url(#globeAtmo)"/>
    <circle cx="170" cy="110" r="78" fill="url(#globeFill)" stroke="#00D4FF" strokeWidth="1" opacity="0.85"/>
    {[-45,-22,0,22,45].map(d => {
      const r = Math.sqrt(78*78 - d*d*0.6);
      return <ellipse key={d} cx="170" cy={110+d*0.6} rx={r} ry={Math.abs(d)*0.18+8} fill="none" stroke="#0a2840" strokeWidth="0.7"/>;
    })}
    {[0,45,90,135].map(a => (
      <ellipse key={a} cx="170" cy="110" rx={Math.cos(a*Math.PI/180)*78} ry="78" fill="none" stroke="#0a2840" strokeWidth="0.7"/>
    ))}
    <path d="M138,80 Q148,75 155,82 Q162,88 158,95 Q152,100 144,97 Q137,92 138,80 Z" fill="#0f3a1a" opacity="0.8"/>
    <path d="M160,85 Q172,78 182,80 Q196,82 200,92 Q202,105 196,112 Q188,118 178,114 Q165,108 162,98 Q158,90 160,85 Z" fill="#0f3a1a" opacity="0.8"/>
    <path d="M155,115 Q162,112 168,118 Q172,128 168,135 Q162,140 156,136 Q150,130 152,122 Z" fill="#0f3a1a" opacity="0.7"/>
    <path d="M196,88 Q204,85 210,90 Q215,98 212,105 Q207,110 200,107 Q194,102 194,95 Z" fill="#0f3a1a" opacity="0.7"/>
    {[
      { cx:148, cy:88,  col:'#00D4FF', label:'12,400 vuelos activos', lx:60,  ly:52  },
      { cx:202, cy:95,  col:'#10b981', label:'Cap $2.4T globales',    lx:240, ly:42  },
      { cx:163, cy:120, col:'#ef4444', label:'Sismo M4.2 detectado', lx:60,  ly:170 },
      { cx:195, cy:105, col:'#FFD600', label:'Fear & Greed: 28',      lx:235, ly:175 },
      { cx:155, cy:97,  col:'#10b981', label:'2,840 noticias hoy',    lx:60,  ly:110 },
    ].map((p,i) => (
      <g key={i} filter="url(#pingGlow)">
        <circle cx={p.cx} cy={p.cy} r="4" fill={p.col} opacity="0.95"/>
        <circle cx={p.cx} cy={p.cy} r="9"  fill="none" stroke={p.col} strokeWidth="0.8" opacity="0.5"/>
        <circle cx={p.cx} cy={p.cy} r="15" fill="none" stroke={p.col} strokeWidth="0.4" opacity="0.2"/>
        <line x1={p.cx} y1={p.cy} x2={p.lx + (p.lx < 170 ? 60 : 0)} y2={p.ly}
          stroke={p.col} strokeWidth="0.7" strokeDasharray="3 2" opacity="0.6"/>
        <rect x={p.lx < 170 ? p.lx : p.lx - 4} y={p.ly - 8} width="76" height="12" rx="3"
          fill="#06101d" stroke={p.col} strokeWidth="0.5" fillOpacity="0.9"/>
        <text x={p.lx < 170 ? p.lx + 38 : p.lx + 34} y={p.ly}
          fill={p.col} fontSize="7" fontFamily="monospace" textAnchor="middle">{p.label}</text>
      </g>
    ))}
    <text x="170" y="210" fill="#52525B" fontSize="8" fontFamily="monospace" textAnchor="middle">Datos globales ingestados en tiempo real · Motor Atenea</text>
  </svg>
);

/* ─────────────────────────────────────────────
   7. RADAR SCANNER
───────────────────────────────────────────── */
const RadarScanner = () => (
  <svg viewBox="0 0 340 200" fill="none" width="100%">
    <defs>
      <radialGradient id="radarBg" cx="50%" cy="50%"><stop offset="0%" stopColor="#06101d"/><stop offset="100%" stopColor="#030810"/></radialGradient>
      <radialGradient id="sweepG" cx="50%" cy="80%">
        <stop offset="0%" stopColor="#10b981" stopOpacity="0.5"/>
        <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
      </radialGradient>
      <filter id="rGlow"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <circle cx="130" cy="100" r="88" fill="url(#radarBg)" stroke="#0f3a2f" strokeWidth="1.2"/>
    {[22,44,66,88].map(r => (
      <circle key={r} cx="130" cy="100" r={r} fill="none" stroke="#0f3a2f" strokeWidth="0.8"/>
    ))}
    <line x1="130" y1="12"  x2="130" y2="188" stroke="#0f3a2f" strokeWidth="0.8"/>
    <line x1="42"  y1="100" x2="218" y2="100" stroke="#0f3a2f" strokeWidth="0.8"/>
    <line x1="68"  y1="38"  x2="192" y2="162" stroke="#0f3a2f" strokeWidth="0.5"/>
    <line x1="192" y1="38"  x2="68"  y2="162" stroke="#0f3a2f" strokeWidth="0.5"/>
    <path d="M130,100 L130,12 A88,88 0 0,1 218,100 Z" fill="url(#sweepG)" opacity="0.7"/>
    <line x1="130" y1="100" x2="218" y2="100" stroke="#10b981" strokeWidth="1.5" opacity="0.8" filter="url(#rGlow)"/>
    {[
      { cx:160, cy:62,  sym:'BTC',   sig:'▲ +2.4%', col:'#10b981' },
      { cx:178, cy:88,  sym:'ETH',   sig:'▲ +3.1%', col:'#10b981' },
      { cx:105, cy:72,  sym:'SOL',   sig:'▲ +6.8%', col:'#FFD600' },
      { cx:150, cy:110, sym:'BNB',   sig:'▼ -0.4%', col:'#ef4444' },
      { cx:88,  cy:120, sym:'XRP',   sig:'▲ +1.2%', col:'#10b981' },
    ].map((s,i) => (
      <g key={i} filter="url(#rGlow)">
        <circle cx={s.cx} cy={s.cy} r="4" fill={s.col} opacity="0.9"/>
        <circle cx={s.cx} cy={s.cy} r="8" fill="none" stroke={s.col} strokeWidth="0.8" opacity="0.4"/>
        <text x={s.cx+6} y={s.cy-2} fill={s.col} fontSize="7" fontFamily="monospace" fontWeight="bold">{s.sym}</text>
        <text x={s.cx+6} y={s.cy+7} fill={s.col} fontSize="6"   fontFamily="monospace">{s.sig}</text>
      </g>
    ))}
    <rect x="234" y="10" width="98" height="180" rx="6" fill="#06101d" stroke="#0f2845" strokeWidth="0.8"/>
    <text x="283" y="26" fill="#10b981" fontSize="7.5" fontFamily="monospace" textAnchor="middle" fontWeight="bold">SENALES HOY</text>
    {[
      ['SOL/USDT','Breakout','96%','#10b981'],
      ['ETH/USDT','Momentum','88%','#10b981'],
      ['BTC/USDT','VWAP Dev','82%','#10b981'],
      ['AVAX/USDT','Mean Rev','74%','#FFD600'],
      ['XRP/USDT','Grid','68%','#FFD600'],
      ['LINK/USDT','Scalp','61%','#A1A1AA'],
    ].map(([sym,strat,conf,col], i) => (
      <g key={i}>
        <text x="240" y={42+i*24} fill="white"   fontSize="6.5" fontFamily="monospace">{sym}</text>
        <text x="240" y={52+i*24} fill="#52525B" fontSize="6"   fontFamily="monospace">{strat}</text>
        <rect x="296" y={34+i*24} width="30" height="10" rx="2" fill={col} fillOpacity="0.15" stroke={col} strokeWidth="0.5"/>
        <text x="311" y={42+i*24} fill={col} fontSize="6.5" fontFamily="monospace" textAnchor="middle">{conf}</text>
      </g>
    ))}
    <text x="283" y="182" fill="#52525B" fontSize="6.5" fontFamily="monospace" textAnchor="middle">100+ pares · 5 TF · Tiempo real</text>
  </svg>
);

/* ─────────────────────────────────────────────
   8. NEWS BOARD
───────────────────────────────────────────── */
const NewsBoard = () => (
  <div className="w-full space-y-2">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex items-center gap-1.5 bg-[#ef4444]/15 border border-[#ef4444]/30 rounded-full px-3 py-1">
        <div className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse"/>
        <span className="text-[#ef4444] text-[9px] font-bold tracking-widest">EN VIVO</span>
      </div>
      <span className="text-[#52525B] text-[9px]">2,840 noticias procesadas hoy</span>
    </div>
    {[
      { h:'Bitcoin supera $65K mientras instituciones acumulan posiciones', t:'hace 12 min', sent:92, col:'#10b981', impact:'ALTO' },
      { h:'Fed mantiene tasas — mercados cripto reactivos al alza', t:'hace 38 min', sent:78, col:'#10b981', impact:'ALTO' },
      { h:'Ethereum: actualizacion Pectra aumenta throughput en 40%', t:'hace 1h', sent:85, col:'#10b981', impact:'MEDIO' },
      { h:'SEC aprueba 3 nuevos ETFs de cripto spot en EE.UU.', t:'hace 2h', sent:88, col:'#10b981', impact:'CRITICO' },
      { h:'Reguladores UE piden mas controles KYC en DeFi', t:'hace 3h', sent:28, col:'#ef4444', impact:'BAJO' },
    ].map((n, i) => (
      <div key={i} className="bg-[#06101d] border border-[#0f2845] rounded-xl px-3 py-2.5">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-white text-[10px] leading-snug">{n.h}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[#52525B] text-[9px]">{n.t}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                style={{ background:`${n.col}15`, color:n.col, border:`0.5px solid ${n.col}30` }}>
                {n.impact}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-center ml-2">
            <p className="text-[8px] text-[#52525B]">SENT</p>
            <p className="font-bold text-xs" style={{ color: n.col }}>{n.sent}</p>
            <div className="w-8 h-1.5 bg-[#0f2845] rounded-full mt-1">
              <div className="h-full rounded-full" style={{ width:`${n.sent}%`, background:n.col }}/>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   9. COPY LEADERBOARD
───────────────────────────────────────────── */
const CopyLeader = () => (
  <div className="w-full space-y-3">
    <div className="flex items-center justify-between mb-3">
      <p className="text-[#A1A1AA] text-xs">Traders verificados por Aegis</p>
      <span className="text-[9px] bg-[#10b981]/10 border border-[#10b981]/25 text-[#10b981] px-2 py-0.5 rounded-full">24 activos</span>
    </div>
    {[
      { rank:1, name:'CryptoMaster_X', syms:'BTC · ETH · SOL', wr:'74%', pnl:'+$12,840', month:'+38.2%', seg:89, bars:[40,55,70,60,80,72,90,95], col:'#FFD600' },
      { rank:2, name:'AltcoinPro_99',  syms:'SOL · ADA · DOT', wr:'68%', pnl:'+$8,290',  month:'+24.7%', seg:62, bars:[30,48,42,65,55,70,65,78], col:'#C0C0C0' },
      { rank:3, name:'ScalpKing_BTC',  syms:'BTC · ETH',       wr:'71%', pnl:'+$6,180',  month:'+19.4%', seg:41, bars:[25,38,50,44,58,62,54,70], col:'#CD7F32' },
    ].map((t) => (
      <div key={t.rank} className="bg-[#06101d] border border-[#0f2845] rounded-xl p-3">
        <div className="flex items-center gap-3">
          <span className="text-xl font-black shrink-0" style={{ color: t.col }}>#{t.rank}</span>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0f2845] to-[#163a5f] flex items-center justify-center font-bold text-sm text-white border border-[#163a5f]">
            {t.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-bold">{t.name}</p>
            <p className="text-[#52525B] text-[9px]">{t.syms}</p>
            <p className="text-[#A1A1AA] text-[9px]">WR <span className="text-white">{t.wr}</span> · {t.seg} seguidores</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[#10b981] font-bold text-sm font-mono">{t.pnl}</p>
            <p className="text-[#10b981] text-[9px]">{t.month} este mes</p>
            <svg width="56" height="20" className="mt-1">
              {t.bars.map((v, bi) => (
                <rect key={bi} x={bi*7} y={20-(v/100)*18} width="5" height={(v/100)*18} rx="1" fill="#10b981" opacity="0.7"/>
              ))}
            </svg>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   10. PAPER TRADING
───────────────────────────────────────────── */
const PaperVault = () => (
  <div className="w-full">
    <div className="relative bg-[#06101d] border border-[#0f2845] rounded-2xl overflow-hidden">
      <div className="absolute top-3 right-3 bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-full px-3 py-1">
        <span className="text-[#00D4FF] text-[9px] font-bold tracking-widest">PAPER MODE</span>
      </div>
      <div className="p-6 text-center border-b border-[#0f2845]">
        <p className="text-[#52525B] text-[10px] uppercase tracking-widest mb-2">Saldo Virtual</p>
        <p className="text-5xl font-black text-[#10b981] font-mono" style={{textShadow:'0 0 30px rgba(16,185,129,0.4)'}}>$10,000</p>
        <p className="text-[#52525B] text-xs mt-1">USDT · Sin riesgo · Sin deposito</p>
      </div>
      <div className="grid grid-cols-2 divide-x divide-[#0f2845]">
        {[['P&L ACUMULADO','+$2,480','#10b981'],['WIN RATE','67.3%','#00D4FF'],['TRADES','142','#A1A1AA'],['RACHA','8W / 2L','#10b981']].map(([l,v,c]) => (
          <div key={l} className="p-4 text-center border-b border-[#0f2845]">
            <p className="text-[#52525B] text-[8px] uppercase tracking-wider">{l}</p>
            <p className="font-bold text-base font-mono mt-1.5" style={{color:c}}>{v}</p>
          </div>
        ))}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[#52525B] text-[10px]">Progreso hacia trading real</p>
          <p className="text-[#10b981] text-[10px] font-bold">74%</p>
        </div>
        <div className="w-full h-2 bg-[#0f2845] rounded-full">
          <div className="h-full w-[74%] bg-gradient-to-r from-[#10b981] to-[#00D4FF] rounded-full"
            style={{boxShadow:'0 0 8px rgba(16,185,129,0.6)'}}/>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {['Gratis','Sin tarjeta','Igual que real','Para siempre'].map(t => (
            <span key={t} className="text-[#10b981] text-[9px] px-2 py-1 rounded-full"
              style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)'}}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   SECCIONES
───────────────────────────────────────────── */
const SECTIONS = [
  {
    id: 'trading', tag: 'TRADING MANUAL', color: '#00D4FF', reverse: false,
    title: 'Un terminal de trading profesional en tu navegador',
    desc: 'Conecta tus exchanges vía API con permisos de trading únicamente. Ejecuta órdenes market, limit, stop-loss y take-profit desde una interfaz diseñada para traders serios. Gráficas de velas con indicadores técnicos, libro de órdenes en vivo y gestión completa de posiciones.',
    bullets: ['14 exchanges: Binance, Bybit, OKX, KuCoin, Bitunix, Gate.io y más','Órdenes spot y futuros con leverage configurable','Indicadores: EMA, Bollinger, VWAP, RSI, MACD','Aegis nunca custodia ni retira tus fondos'],
    Visual: TradingTerminal,
  },
  {
    id: 'neural', tag: 'MODELO NEURONAL · PPO', color: '#10b981', reverse: true,
    title: 'La IA que aprendió a operar con 400,000 datos reales',
    desc: 'Aegis-IA0003 es un agente de aprendizaje por refuerzo (PPO) entrenado con Stable-Baselines3 y Keras sobre 400,000+ velas históricas reales de BTC/USDT. Identifica el régimen de mercado — tendencia, lateral o volátil — y adapta cada decisión en tiempo real.',
    bullets: ['PPO + Keras + Stable-Baselines3 sobre datos históricos reales','Re-entrenamiento automático cada 30 días con datos nuevos','Detección de régimen: tendencia / lateral / volátil / explosivo','Gestión de riesgo integrada: tamaño de posición dinámico'],
    Visual: NeuralBrain,
  },
  {
    id: 'atenea', tag: 'CEREBRO ATENEA', color: '#10b981', reverse: false,
    title: 'La inteligencia artificial que coordina todo',
    desc: 'Atenea es el motor central de Aegis. Analiza el régimen del mercado cada minuto, evalúa qué bots activar, conversa contigo en lenguaje natural, predice precios y genera código Python para backtests al instante. También lee noticias y ajusta su visión del mercado en tiempo real.',
    bullets: ['Chat IA: "predice BTC a 24h", "evalúa mis bots activos"','Genera estrategias desde descripción en texto natural','Código Python de backtest generado al instante','Análisis de sentimiento de noticias en tiempo real'],
    Visual: AtenеaChat,
  },
  {
    id: 'arbitrage', tag: 'ARBITRAJE', color: '#FFD600', reverse: true,
    title: 'Captura spreads entre exchanges antes que nadie',
    desc: 'El motor de arbitraje monitorea diferencias de precio entre spot y futuros perpetuos, y entre exchanges distintos. Detecta oportunidades en milisegundos, calcula el profit neto después de comisiones y funding rates, y ejecuta las dos piernas de forma simultánea.',
    bullets: ['Arbitraje spot-futuro: captura el basis de funding','Arbitraje futuro-futuro entre exchanges en paralelo','Cálculo de ganancia neta real (comisiones + funding incluidos)','Ejecución paralela de órdenes en menos de 200ms'],
    Visual: ArbFlow,
  },
  {
    id: 'bots', tag: 'BOTS 24/7', color: '#00D4FF', reverse: false,
    title: '15+ estrategias operando sin descanso mientras duermes',
    desc: 'Crea bots con estrategias probadas o diseña las tuyas con Atenea. El Guardian protege tu capital pausando bots ante racha perdedora. Cada bot tiene historial completo y métricas de rendimiento. Comienzan en paper y se activan en real cuando tú confirmes.',
    bullets: ['VWAP, Momentum, Grid, Scalping, Mean Reversion Z, Breakout','Guardian: pausa automática ante racha perdedora','Historial completo con P&L, win rate y estadísticas','Alertas en tiempo real por notificación en la plataforma'],
    Visual: BotCenter,
  },
  {
    id: 'livedata', tag: 'DATOS EN VIVO GLOBALES', color: '#00D4FF', reverse: true,
    title: 'La IA que entiende el mundo, no solo el precio',
    desc: 'Aegis ingesta feeds globales en tiempo real para que Atenea entienda el contexto macroeconómico más allá del gráfico. Vuelos activos, sismos, capitalización de mercado global, índice Fear & Greed y miles de noticias — todo procesado por el modelo neuronal.',
    bullets: ['Índice Fear & Greed actualizado cada hora','Capitalización global y dominancia BTC en tiempo real','12,000+ vuelos activos y actividad sísmica mundial','Noticias cripto con análisis de sentimiento automático'],
    Visual: LiveGlobe,
  },
  {
    id: 'scanner', tag: 'SCANNER DE MERCADO', color: '#10b981', reverse: false,
    title: 'Encuentra la oportunidad antes de que el mercado se mueva',
    desc: 'El scanner evalúa más de 100 pares en 5 timeframes aplicando todas las estrategias simultáneamente. Atenea filtra el ruido y clasifica las señales por confianza y régimen de mercado. La mejor señal aparece destacada para que puedas actuar de inmediato.',
    bullets: ['100+ pares escaneados · 5 timeframes simultáneos','Ranking por confianza y condición de mercado actual','Filtro por estrategia, exchange y par de interés','Crear bot directamente desde una señal con 1 clic'],
    Visual: RadarScanner,
  },
  {
    id: 'news', tag: 'NOTICIAS Y SENTIMIENTO', color: '#FFD600', reverse: true,
    title: 'El mercado reacciona a noticias — Atenea también',
    desc: 'Feed de noticias cripto en tiempo real con análisis de sentimiento por IA. Atenea procesa cada noticia, calcula su impacto potencial y ajusta su evaluación del mercado automáticamente. Tú ves el contexto completo; los bots ya están aplicando el ajuste.',
    bullets: ['Miles de noticias cripto procesadas cada día por IA','Puntuación de sentimiento 0–100 por noticia en tiempo real','Clasificación por impacto: CRÍTICO, ALTO, MEDIO, BAJO','Atenea ajusta estrategias basándose en el sentimiento global'],
    Visual: NewsBoard,
  },
  {
    id: 'copy', tag: 'COPY TRADING', color: '#10b981', reverse: false,
    title: 'Opera como los mejores traders sin experiencia previa',
    desc: 'Sigue a traders verificados por Aegis con historial real de operaciones. Cuando el trader abre una posición, tu cuenta la replica automáticamente de forma proporcional. El sistema aplica las mismas reglas de riesgo y puedes parar en cualquier momento.',
    bullets: ['Traders verificados con historial real y auditable','Copia proporcional automática a tu capital disponible','P&L en tiempo real de cada trader que sigues','Parar cualquier copia con un solo clic'],
    Visual: CopyLeader,
  },
  {
    id: 'paper', tag: 'PAPER TRADING', color: '#A1A1AA', reverse: true,
    title: 'Aprende, practica y perfecciona — sin arriesgar un centavo',
    desc: 'Todo usuario de Aegis empieza con $10,000 USDT virtuales. El entorno es idéntico al modo real: mismos exchanges, mismas estrategias, mismo P&L tracking. Cuando domines el sistema, activas el modo real con un solo clic. Sin depósito previo.',
    bullets: ['$10,000 USDT virtuales desde el primer minuto','Mismo entorno que el modo real — sin ninguna diferencia','P&L e historial completos igual que en real','Gratuito, sin tarjeta de crédito, sin vencimiento'],
    Visual: PaperVault,
  },
];

/* ─────────────────────────────────────────────
   SOCIAL ICONS
───────────────────────────────────────────── */
const SocialX  = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.254 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
const SocialYT = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>;
const SocialIG = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>;
const SocialTG = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>;

/* ─────────────────────────────────────────────
   MAIN LANDING
───────────────────────────────────────────── */
const Landing = () => {
  const refCode = new URLSearchParams(window.location.search).get('ref');

  useEffect(() => {
    document.body.classList.add('matrix-page');
    return () => document.body.classList.remove('matrix-page');
  }, []);

  return (
    <div className="min-h-screen bg-[#06101d] relative overflow-x-hidden">
      <MatrixRain />

      {/* ── HERO ── */}
      <div className="relative z-10">
        <div className="max-w-5xl mx-auto px-4 pt-16 pb-20 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#00D4FF]/50" />
            <span className="text-[10px] text-[#00D4FF] uppercase tracking-[0.4em] font-medium">Powered by AI</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#00D4FF]/50" />
          </div>
          <h1 className="text-7xl md:text-9xl font-black tracking-tighter mb-2" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #a8d8ff 50%, #00D4FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>AEGIS</h1>
          <p className="text-sm text-[#00D4FF]/70 uppercase tracking-[0.45em] mb-1 font-light">Trading Platform</p>
          <p className="text-[#7a90a8] italic text-sm mb-10">El escudo que porta Atenea</p>
          <p className="text-base text-[#7a90a8] mb-10 max-w-xl mx-auto leading-relaxed">
            Inteligencia artificial, modelo neuronal PPO y 15+ bots autónomos operando 24/7 en 14 exchanges — con tu dinero siempre en tu cuenta.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to={refCode ? `/register?ref=${refCode}` : '/register'}>
              <Button className="bg-[#10b981] text-black hover:bg-[#34d399] font-bold uppercase tracking-wider text-sm px-12 py-7 rounded-sm transition-all"
                style={{ boxShadow: '0 0 35px rgba(16,185,129,0.45)' }}
                data-testid="landing-get-started-button">
                Registro <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
            <Link to="/login">
              <Button className="bg-transparent text-white border border-[#27272A] hover:border-[#10b981] hover:text-[#10b981] uppercase tracking-wider text-sm px-12 py-7 rounded-sm transition-all"
                data-testid="landing-login-button">
                Iniciar Sesión
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="relative z-10 border-y border-[#10b981]/15 bg-[#0a1828]/60 backdrop-blur-sm py-6">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
          {[['15+','Estrategias IA'],['14','Exchanges'],['400K+','Datos de entrenamiento'],['24/7','Operación autónoma'],['0%','Custodia de fondos']].map(([v, l]) => (
            <div key={l}>
              <p className="text-2xl font-bold text-[#10b981] font-mono">{v}</p>
              <p className="text-[10px] text-[#52525B] mt-1 uppercase tracking-wider">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURE SECTIONS ── */}
      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {SECTIONS.map(({ id, tag, title, desc, bullets, Visual, color, reverse }) => (
          <div key={id}
            className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center py-20 border-b border-[#0f2845]/60`}>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-bold tracking-widest px-3 py-1.5 rounded-full border font-mono"
                style={{ color, borderColor:`${color}35`, background:`${color}0d` }}>
                {tag}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-5 mb-4 leading-snug">{title}</h2>
              <p className="text-[#6a8098] text-sm leading-relaxed mb-6">{desc}</p>
              <ul className="space-y-2.5">
                {bullets.map(b => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-[#A1A1AA]">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full"
                      style={{ background:color, boxShadow:`0 0 6px ${color}` }}/>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 min-w-0 w-full max-w-md">
              <div className="rounded-2xl p-4 border"
                style={{ background:'#06101d', borderColor:`${color}18`, boxShadow:`0 0 30px ${color}08` }}>
                <div style={{ fontSize: '0.82em' }}>
                  <Visual />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── SECURITY BANNER ── */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-20">
        <div className="rounded-2xl p-10 text-center border"
          style={{ background:'linear-gradient(135deg,#06101d,#0a1828)', borderColor:'rgba(16,185,129,0.3)',
            boxShadow:'0 0 60px rgba(16,185,129,0.08), inset 0 1px 0 rgba(16,185,129,0.1)' }}>
          <svg viewBox="0 0 60 68" fill="none" className="w-16 h-16 mx-auto mb-5">
            <path d="M30 2L54 14v24C54 50 43 62 30 66 17 62 6 50 6 38V14Z" stroke="#10b981" strokeWidth="2.5" fill="#10b981" fillOpacity="0.08"/>
            <polyline points="18,34 27,43 42,24" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3 className="text-white font-bold text-3xl mb-4">Tu dinero nunca sale de tu exchange</h3>
          <p className="text-[#6a8098] text-sm max-w-lg mx-auto leading-relaxed">
            Aegis se conecta mediante claves API con permisos exclusivos de trading — sin acceso a retiros. Tu capital permanece 100% bajo tu control en todo momento.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {['Sin custodia de fondos','Sin permisos de retiro','API trade-only','Cifrado AES-256'].map(t => (
              <span key={t} className="text-xs px-4 py-2 rounded-full font-medium"
                style={{ color:'#10b981', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.25)' }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div className="relative z-10 border-t border-[#0f2845] py-10 bg-[#06101d]">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/images/aegis-logo.png"
              alt="Aegis"
              style={{ width: 64, mixBlendMode: 'screen', filter: 'brightness(1.1)' }}
            />
            <p className="text-[#52525B] text-[11px]">© 2026 Aegis Trading Platform</p>
          </div>
          <div className="flex items-center gap-3">
            {[
              { Icon: SocialX,  href:'https://x.com/aegistradingIA',         label:'X',         col:'#ffffff' },
              { Icon: SocialYT, href:'https://youtube.com/@aegistradingIA',   label:'YouTube',   col:'#FF0000' },
              { Icon: SocialIG, href:'https://instagram.com/aegistradingIA',  label:'Instagram', col:'#E1306C' },
              { Icon: SocialTG, href:'https://t.me/aegistradingIA',           label:'Telegram',  col:'#2AABEE' },
            ].map(({ Icon, href, label, col }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" title={label}
                className="w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 text-[#A1A1AA]"
                style={{ background:'#0a1828', borderColor:'#163a5f' }}
                onMouseEnter={e => { e.currentTarget.style.color=col; e.currentTarget.style.borderColor=col; e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow=`0 0 12px ${col}40`; }}
                onMouseLeave={e => { e.currentTarget.style.color='#A1A1AA'; e.currentTarget.style.borderColor='#163a5f'; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                <Icon />
              </a>
            ))}
          </div>
          <p className="text-[10px] text-[#27272A] text-center leading-relaxed">
            El trading involucra riesgo de pérdida significativa.<br/>No somos asesores financieros registrados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Landing;
